import { df, firebase } from '@/plugins/firebase'

/**
 * Universal billing record metoda
 *
 * @param {Object} params - Billing parametri
 * @param {string} params.company_id - ID tvrtke
 * @param {string} params.category - Kategorija usluge (orderman, warehouse, itd.)
 * @param {string} params.user_id - ID korisnika koji poziva
 * @param {string} params.action - 'activate' ili 'deactivate'
 * @param {string} [params.id] - Opciono: ID zapisa za update
 * @param {Object} [params.custom_data] - Opciono: Dodatni podaci (JSON)
 * @returns {Promise<string>} - Document ID kreiranog/updateanog zapisa
 */
export const recordBilling = async ({
  company_id,
  category,
  user_id,
  action,
  id = null,
  custom_data = {}
}) => {
  try {
    // Validacija parametara
    if (!company_id || !category || !user_id || !action) {
      throw new Error('Missing required billing parameters')
    }

    if (!['activate', 'deactivate'].includes(action)) {
      throw new Error('Invalid action. Must be "activate" or "deactivate"')
    }

    const timestamp = firebase.firestore.FieldValue.serverTimestamp()
    const billingRef = df
      .collection('billing')
      .doc(company_id)
      .collection(category)

    if (action === 'activate') {
      // Kreiranje novog zapisa
      const data = {
        activate_dt: timestamp,
        deactivate_dt: null,
        user_id: user_id,
        custom_data: custom_data || {}
      }

      // Ako je ID proslijeđen, dodaj ga
      if (id) {
        data.id = id
      }

      const docRef = await billingRef.add(data)

      console.log(`[Billing] Activation recorded: ${category} for company ${company_id}`)
      return docRef.id

    } else if (action === 'deactivate') {
      // Update postojećeg zapisa
      if (!id) {
        throw new Error('ID is required for deactivation')
      }

      // Pronađi zapis sa danim ID-om
      const snapshot = await billingRef
        .where('id', '==', id)
        .where('deactivate_dt', '==', null)
        .get()

      if (snapshot.empty) {
        throw new Error(`No active billing record found with id: ${id}`)
      }

      // Update prvog pronađenog zapisa (trebao bi biti samo jedan)
      const doc = snapshot.docs[0]
      const updateData = {
        deactivate_dt: timestamp
      }

      // Merge custom_data ako je proslijeđen
      if (custom_data && Object.keys(custom_data).length > 0) {
        const existingData = doc.data().custom_data || {}
        updateData.custom_data = {
          ...existingData,
          ...custom_data
        }
      }

      await billingRef.doc(doc.id).update(updateData)

      console.log(`[Billing] Deactivation recorded: ${category} for company ${company_id}`)
      return doc.id
    }
  } catch (error) {
    console.error('[Billing] Error recording billing:', error)
    throw error
  }
}

/**
 * Dohvati aktivne billing zapise za kategoriju
 *
 * @param {string} company_id - ID tvrtke
 * @param {string} category - Kategorija usluge
 * @returns {Promise<Array>} - Lista aktivnih zapisa
 */
export const getActiveBillingRecords = async (company_id, category) => {
  try {
    const snapshot = await df
      .collection('billing')
      .doc(company_id)
      .collection(category)
      .where('deactivate_dt', '==', null)
      .get()

    return snapshot.docs.map(doc => ({
      firestore_id: doc.id,
      ...doc.data()
    }))
  } catch (error) {
    console.error('[Billing] Error fetching active records:', error)
    throw error
  }
}

/**
 * Dohvati billing history za period
 *
 * @param {string} company_id - ID tvrtke
 * @param {string} category - Kategorija usluge
 * @param {Date} startDate - Početni datum
 * @param {Date} endDate - Završni datum
 * @returns {Promise<Array>} - Lista zapisa
 */
export const getBillingHistory = async (company_id, category, startDate, endDate) => {
  try {
    const snapshot = await df
      .collection('billing')
      .doc(company_id)
      .collection(category)
      .where('activate_dt', '>=', startDate)
      .where('activate_dt', '<=', endDate)
      .orderBy('activate_dt', 'desc')
      .get()

    return snapshot.docs.map(doc => ({
      firestore_id: doc.id,
      ...doc.data()
    }))
  } catch (error) {
    console.error('[Billing] Error fetching history:', error)
    throw error
  }
}
